import React from 'react';
import { useUserProfile } from '@/context/UserProfileContext';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { useState, useEffect } from 'react';
import { checkDebugMode } from '../utils/debugUtils';
import { useAppNavigation } from '@/lib/i18nNavigation';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { Theme, applyTheme, getInitialTheme } from '@/utils/theme';
import env from '@/utils/loadEnv';

type TabType = 'auth' | 'profile' | 'device' | 'navigation' | 'theme' | 'env' | 'firebase' | 'settings';

type BannerPosition = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export const DebugBanner: React.FC = () => {
  const { profile } = useUserProfile();
  const [authUser, setAuthUser] = useState<any>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [deviceId, setDeviceId] = useState<string>('');
  const [deviceInfo, setDeviceInfo] = useState<any>(null);
  const [network, setNetwork] = useState<boolean>(false);
  const { getCurrentPathname, navigate } = useAppNavigation();
  const pathname = getCurrentPathname();
  const [activeTab, setActiveTab] = useState<TabType>('navigation');
  const [notificationStatus, setNotificationStatus] = useState<string>('unknown');
  const [currentTheme, setCurrentTheme] = useState<Theme>(getInitialTheme());
  const [bannerPosition, setBannerPosition] = useState<BannerPosition>('top-right');

  // Environment variables
  const envVars = {
    NODE_ENV: import.meta.env.MODE,
    FIREBASE_PROJECT_ID: env.VITE_FIREBASE_PROJECT_ID,
    FIREBASE_AUTH_DOMAIN: env.VITE_FIREBASE_AUTH_DOMAIN,
    SENTRY_DSN: env.VITE_SENTRY_DSN,
    PUBLIC_API_URL: env.VITE_PUBLIC_API_URL,
    APP_URL: env.VITE_APP_URL,
  };
  FirebaseAuthentication.addListener('authStateChange', (result) => {
    console.log("Auth state changed:", result.user ? 'Logged in' : 'Logged out')
    setAuthUser(result.user);
  });

  useEffect(() => {
    const checkDebugState = async () => {
      // Use the new debug utility to check debug mode
      const isDebug = await checkDebugMode(true);
      setIsDebugMode(isDebug);

      // Load saved position if available
      const { value: positionValue } = await Preferences.get({ key: 'debugBannerPosition' });
      if (positionValue) {
        setBannerPosition(positionValue as BannerPosition);
      }
    };

    const checkAuth = async () => {
      try {
        const result = await FirebaseAuthentication.getCurrentUser();
        setAuthUser(result.user);
      } catch (error) {
        console.error('Error getting current user:', error);
      }
    };

    const getDeviceDetails = async () => {
      try {
        const deviceId = await Device.getId();
        const deviceInfo = await Device.getInfo();
        setDeviceId(deviceId.identifier);
        setDeviceInfo(deviceInfo);
      } catch (error) {
        console.error('Error getting device details:', error);
      }
    };

    const checkNetworkStatus = async () => {
      try {
        const status = await Network.getStatus();
        setNetwork(status.connected);
        console.log('Network status:', status.connected ? 'Connected' : 'Disconnected');
      } catch (error) {
        console.error('Error checking network status:', error);
        setNetwork(false);
      }
    };

    // Get current theme
    const savedTheme = getInitialTheme();
    setCurrentTheme(savedTheme);

    checkDebugState();
    checkAuth();
    getDeviceDetails();
    checkNetworkStatus();

    // Check notification permission status
    const checkNotificationStatus = async () => {
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("FirebaseMessaging")) {
        try {
          const permissionStatus = await FirebaseMessaging.checkPermissions();
          setNotificationStatus(permissionStatus.receive);
        } catch (error) {
          console.error('Error checking notification permissions:', error);
          setNotificationStatus('error');
        }
      } else {
        setNotificationStatus('not available');
      }
    };

    checkNotificationStatus();

    // Set up listener for auth state changes
    const setupListener = async () => {
      const listener = await FirebaseAuthentication.addListener('authStateChange', (result) => {
        setAuthUser(result.user);
      });

      return listener;
    };

    const listenerPromise = setupListener();

    // Set up network status change listener
    const setupNetworkListener = async () => {
      return await Network.addListener('networkStatusChange', status => {
        console.log('Network status changed:', status.connected ? 'Connected' : 'Disconnected');
        setNetwork(status.connected);
      });
    };

    const networkListenerPromise = setupNetworkListener();

    return () => {
      // Clean up listeners
      listenerPromise.then(() => {
        FirebaseAuthentication.removeAllListeners();
      }).catch(err => console.error('Error removing auth listener:', err));

      networkListenerPromise.then(listener => {
        listener.remove();
      }).catch(err => console.error('Error removing network listener:', err));
    };
  }, []);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Save position to preferences
  const savePosition = async (position: BannerPosition) => {
    await Preferences.set({ key: 'debugBannerPosition', value: position });
    setBannerPosition(position);
  };

  // Get position classes based on current position
  const getPositionClasses = () => {
    if (isExpanded) {
      return 'left-0 right-0 bg-slate-600 opacity-90 ' +
        (bannerPosition.startsWith('top') ? 'top-safe' : 'bottom-safe');
    }

    switch (bannerPosition) {
      case 'top-left':
        return 'top-safe left-4 w-12 h-12 flex items-center justify-center';
      case 'top-right':
        return 'top-safe right-4 w-12 h-12 flex items-center justify-center';
      case 'bottom-left':
        return 'bottom-safe-offset-10 left-4 w-12 h-12 flex items-center justify-center';
      case 'bottom-right':
        return 'bottom-safe-offset-10 right-4 w-12 h-12 flex items-center justify-center';
      default:
        return 'top-safe right-4 w-12 h-12 flex items-center justify-center';
    }
  };

  if (isDebugMode === false) return null;
  return (
    <div
      className={`fixed z-50 text-white text-xs ${getPositionClasses()}`}
      style={{
        maxHeight: isExpanded ? '480px' : 'auto',
        overflow: isExpanded ? 'auto' : 'hidden',
        transition: 'all 0.3s ease'
      }}
    >
      {isExpanded ? (
        <div className="flex justify-between items-center px-2 py-1 cursor-pointer" onClick={toggleExpand}>
          <div className="z-50 rounded-bl px-2 font-mono text-white">
            <span className="sm:hidden">default</span>
            <span className="hidden sm:inline md:hidden">sm</span>
            <span className="hidden md:inline lg:hidden">md</span>
            <span className="hidden lg:inline xl:hidden">lg</span>
            <span className="hidden xl:inline">xl</span>
          </div>
          <div>
            SignedIn: {authUser ? '✅' : '❌'} |
            Profile: {profile ? '✅' : '❌'} |
            Network: {network ? '✅' : '❌'}
          </div>
          <div>▼</div>
        </div>
      ) : (
        <div
          className="w-full h-full flex items-center justify-center cursor-pointer"
          onClick={toggleExpand}
        >
          <span className="text-lg font-bold rounded-full bg-red-600 w-12 h-12 flex items-center justify-center">🛠️</span>
        </div>
      )}

      {isExpanded && (
        <div className="border-t border-slate-400 bg-slate-700">
          {/* Terminal-like tabs */}
          <div className="flex flex-wrap border-b border-slate-500">
            <div
              className={`px-3 py-1 cursor-pointer ${activeTab === 'navigation' ? 'bg-slate-600 border-b-2 border-blue-400' : 'bg-slate-800'}`}
              onClick={() => setActiveTab('navigation')}
            >
              Navigation
            </div>
            <div
              className={`px-3 py-1 cursor-pointer ${activeTab === 'auth' ? 'bg-slate-600 border-b-2 border-blue-400' : 'bg-slate-800'}`}
              onClick={() => setActiveTab('auth')}
            >
              User
            </div>
            <div
              className={`px-3 py-1 cursor-pointer ${activeTab === 'profile' ? 'bg-slate-600 border-b-2 border-blue-400' : 'bg-slate-800'}`}
              onClick={() => setActiveTab('profile')}
            >
              Profile
            </div>
            <div
              className={`px-3 py-1 cursor-pointer ${activeTab === 'device' ? 'bg-slate-600 border-b-2 border-blue-400' : 'bg-slate-800'}`}
              onClick={() => setActiveTab('device')}
            >
              Device
            </div>
            <div
              className={`px-3 py-1 cursor-pointer ${activeTab === 'theme' ? 'bg-slate-600 border-b-2 border-blue-400' : 'bg-slate-800'}`}
              onClick={() => setActiveTab('theme')}
            >
              Theme
            </div>
            <div
              className={`px-3 py-1 cursor-pointer ${activeTab === 'env' ? 'bg-slate-600 border-b-2 border-blue-400' : 'bg-slate-800'}`}
              onClick={() => setActiveTab('env')}
            >
              Env
            </div>
            <div
              className={`px-3 py-1 cursor-pointer ${activeTab === 'settings' ? 'bg-slate-600 border-b-2 border-blue-400' : 'bg-slate-800'}`}
              onClick={() => setActiveTab('settings')}
            >
              Settings
            </div>
          </div>

          {/* Tab content */}
          <div className="p-2">
            {activeTab === 'navigation' && (
              <div>
                <h3 className="font-mono">URL: {pathname}</h3>
                <div className="flex flex-wrap gap-2 my-2">
                  <button
                    onClick={() => navigate('/dashboard')}
                    className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-1 px-3 rounded text-xs">
                    Go to Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/welcome')}
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-1 px-3 rounded text-xs">
                    Go to Welcome
                  </button>
                  <button
                    onClick={() => navigate('/mobile')}
                    className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-1 px-3 rounded text-xs">
                    Mobile
                  </button>
                  <button
                    onClick={() => navigate('/menu')}
                    className="bg-purple-500 hover:bg-purple-700 text-white font-bold py-1 px-3 rounded text-xs">
                    Menu
                  </button>
                  <button
                    onClick={() => navigate('/debug')}
                    className="bg-orange-500 hover:bg-orange-700 text-white font-bold py-1 px-3 rounded text-xs">
                    DEBUG PAGE
                  </button>
                  <button
                    onClick={() => window.location.reload()}
                    className="bg-orange-500 hover:bg-orange-700 text-white font-bold py-1 px-3 rounded text-xs">
                    Refresh
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'auth' && (
              <div>
                <h3 className="font-mono mb-1">Auth Status: {authUser ? '✅ Signed In' : '❌ Not Signed In'}</h3>
                <pre className="text-xs overflow-auto whitespace-pre-wrap bg-slate-800 p-2 rounded font-mono">
                  {authUser ? JSON.stringify(authUser, null, 2) : 'Not signed in'}
                </pre>
              </div>
            )}

            {activeTab === 'profile' && (
              <div>
                <h3 className="font-mono mb-1">Profile Status: {profile ? '✅ Exists' : '❌ Not Found'}</h3>
                <pre className="text-xs overflow-auto whitespace-pre-wrap bg-slate-800 p-2 rounded font-mono">
                  {profile ? JSON.stringify(profile, null, 2) : 'No profile found'}
                </pre>
              </div>
            )}

            {activeTab === 'device' && (
              <div>
                <h3 className="font-mono mb-1">Device ID: {deviceId || 'Unknown'}</h3>
                {deviceInfo && (
                  <pre className="text-xs overflow-auto whitespace-pre-wrap bg-slate-800 p-2 rounded font-mono">
                    {JSON.stringify(deviceInfo, null, 2)}
                  </pre>
                )}
              </div>
            )}

            {activeTab === 'theme' && (
              <div>
                <h3 className="font-mono mb-2">Current Theme: {currentTheme}</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setCurrentTheme('light');
                      applyTheme('light');
                    }}
                    className={`px-3 py-2 rounded text-xs ${currentTheme === 'light'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-black'}`}
                  >
                    Light Mode
                  </button>
                  <button
                    onClick={() => {
                      setCurrentTheme('dark');
                      applyTheme('dark');
                    }}
                    className={`px-3 py-2 rounded text-xs ${currentTheme === 'dark'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-800 text-white'}`}
                  >
                    Dark Mode
                  </button>
                  <button
                    onClick={() => {
                      setCurrentTheme('system');
                      applyTheme('system');
                    }}
                    className={`px-3 py-2 rounded text-xs ${currentTheme === 'system'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-500 text-white'}`}
                  >
                    System Default
                  </button>
                </div>
                <div className="mt-2 text-xs bg-slate-800 p-2 rounded font-mono">
                  <p>Theme settings are saved to localStorage and will persist across sessions.</p>
                  <p className="mt-1">System default uses your device's preferred color scheme.</p>
                </div>
              </div>
            )}

            {activeTab === 'env' && (
              <div>
                <h3 className="font-mono mb-2">Environment Variables</h3>
                <div className="text-xs bg-slate-800 p-2 rounded font-mono">
                  <table className="w-full">
                    <tbody>
                      <tr>
                        <td className="py-1 pr-2 text-blue-300">VITE_SENTRY_DSN:</td>
                        <td>{envVars.SENTRY_DSN || 'Not set'}</td>
                      </tr>
                      <tr>
                        <td className="py-1 pr-2 text-blue-300">VITE_PUBLIC_API_URL:</td>
                        <td>{envVars.PUBLIC_API_URL || 'Not set'}</td>
                      </tr>
                      <tr>
                        <td className="py-1 pr-2 text-blue-300">VITE_APP_URL:</td>
                        <td>{envVars.APP_URL || 'Not set'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <h3 className="font-mono mb-2">Firebase Status</h3>
                <div className="text-xs bg-slate-800 p-2 rounded font-mono">
                  <table className="w-full">
                    <tbody>
                      <tr>
                        <td className="py-1 pr-2 text-blue-300">Project ID:</td>
                        <td>{envVars.FIREBASE_PROJECT_ID || 'Not set'}</td>
                      </tr>
                      <tr>
                        <td className="py-1 pr-2 text-blue-300">Auth Domain:</td>
                        <td>{envVars.FIREBASE_AUTH_DOMAIN || 'Not set'}</td>
                      </tr>
                      <tr>
                        <td className="py-1 pr-2 text-blue-300">Notification Status:</td>
                        <td>
                          <span className={`px-2 py-1 rounded ${notificationStatus === 'granted' ? 'bg-green-700' :
                            notificationStatus === 'denied' ? 'bg-red-700' :
                              notificationStatus === 'prompt' || notificationStatus === 'prompt-with-rationale' ? 'bg-yellow-700' : 'bg-gray-700'}`}>
                            {notificationStatus}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1 pr-2 text-blue-300">Auth Status:</td>
                        <td>
                          <span className={`px-2 py-1 rounded ${authUser ? 'bg-green-700' : 'bg-red-700'}`}>
                            {authUser ? 'Signed In' : 'Signed Out'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div className="mt-2">
                    <button
                      onClick={async () => {
                        if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("FirebaseMessaging")) {
                          try {
                            const permission = await FirebaseMessaging.requestPermissions();
                            setNotificationStatus(permission.receive);
                          } catch (error) {
                            console.error('Error requesting notification permissions:', error);
                          }
                        }
                      }}
                      className="px-3 py-1 rounded-md text-xs bg-blue-600 text-white mr-2"
                    >
                      Request Notification Permission
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div>
                <h3 className="font-mono mb-2">Debug Banner Position</h3>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    onClick={() => savePosition('top-left')}
                    className={`px-3 py-2 rounded text-xs ${bannerPosition === 'top-left' ? 'bg-blue-500' : 'bg-slate-600'}`}
                  >
                    Top Left
                  </button>
                  <button
                    onClick={() => savePosition('top-right')}
                    className={`px-3 py-2 rounded text-xs ${bannerPosition === 'top-right' ? 'bg-blue-500' : 'bg-slate-600'}`}
                  >
                    Top Right
                  </button>
                  <button
                    onClick={() => savePosition('bottom-left')}
                    className={`px-3 py-2 rounded text-xs ${bannerPosition === 'bottom-left' ? 'bg-blue-500' : 'bg-slate-600'}`}
                  >
                    Bottom Left
                  </button>
                  <button
                    onClick={() => savePosition('bottom-right')}
                    className={`px-3 py-2 rounded text-xs ${bannerPosition === 'bottom-right' ? 'bg-blue-500' : 'bg-slate-600'}`}
                  >
                    Bottom Right
                  </button>
                </div>
                <div className="text-xs bg-slate-800 p-2 rounded font-mono">
                  <p>Position settings are saved to device preferences and will persist across sessions.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugBanner;
